import { useRef, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

export const ClosingCTA = () => {
  const ref = useRef(null);
  const { lang } = useLanguage();
  const tx = t(lang).closing;

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const st = { trigger: ref.current, start: 'top 80%' };

      // Logo se pojavi s scale
      gsap.fromTo('.cta-logo',
        { opacity: 0, scale: 0.85, y: 20 },
        { opacity: 1, scale: 1, y: 0, duration: 1.1, ease: 'back.out(1.4)', scrollTrigger: st }
      );
      // Naslov
      gsap.fromTo('.cta-title',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', delay: 0.3, scrollTrigger: st }
      );
      // Podnaslov
      gsap.fromTo('.cta-sub',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.55, scrollTrigger: st }
      );
      // Gumbi
      gsap.fromTo('.cta-btns',
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', delay: 0.75, scrollTrigger: st }
      );
      // KPI-ji z zamikami
      gsap.fromTo('.cta-kpi',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', stagger: 0.1, delay: 1,
          scrollTrigger: st }
      );
      // Radial glow pulse
      gsap.to('.cta-glow',
        { opacity: 0.12, scale: 1.2, duration: 4, ease: 'sine.inOut', repeat: -1, yoyo: true }
      );
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="closing"
      ref={ref}
      data-testid="closing-cta-section"
      className="scroll-section min-h-screen flex items-center px-4 relative overflow-hidden"
    >
      {/* Animated background glow */}
      <div className="cta-glow absolute inset-0 pointer-events-none" style={{ opacity: 0.05 }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(ellipse, rgba(200,117,51,1) 0%, transparent 70%)' }} />
      </div>

      <div className="section-content relative z-10 max-w-3xl mx-auto text-center">

        {/* Logo */}
        <div className="cta-logo flex justify-center mb-10">
          <img src="/wibe-logo.png" alt="Wibe Solutions" className="h-56 w-auto"
            style={{ filter: 'drop-shadow(0 0 40px rgba(200,117,51,0.3))' }} />
        </div>

        <h2 className="cta-title font-display text-2xl sm:text-3xl lg:text-5xl font-bold tracking-tight leading-tight mb-6 text-white">
          {tx.headline1}
          <br />
          <span className="text-copper-gradient">
            {tx.headlineAccent}
          </span>
        </h2>

        <p className="cta-sub text-base sm:text-lg text-white/60 max-w-xl mx-auto mb-10 leading-relaxed">
          {tx.sub}
        </p>

        <div className="cta-btns flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://wibe.si"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#C87533] hover:bg-[#C87533]/90 text-white font-semibold px-8 py-4 rounded-xl text-sm btn-lift flex items-center gap-2"
          >
            {tx.btnPrimary}
            <ArrowRight className="w-4 h-4" />
          </a>
          <button
            className="bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-[#C87533]/40 font-medium px-8 py-4 rounded-xl text-sm transition-colors duration-200"
            onClick={() => {
              gsap.killTweensOf(window);
              gsap.to(window, { scrollTo: 0, duration: 1.4, ease: 'power3.out' });
            }}
          >
            {tx.btnSecondary}
          </button>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-center gap-10">
          {tx.kpis.map((item, i) => (
            <div key={i} className="cta-kpi text-center">
              <p className="kpi-number text-xl sm:text-2xl font-bold text-[#C87533]"
                style={{ textShadow: '0 0 20px rgba(200,117,51,0.4)' }}>
                {item.value}
              </p>
              <p className="font-mono text-[10px] text-white/40 mt-1">{item.label}</p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};
